/**
 * context/BranchScopeContext.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Which of the vendor's locations the dashboard is looking at (4 Oct 2026).
 *
 *   active   "all"   every location this login may see (the whole business
 *                    for head office, every assigned branch for a member)
 *            "main"  the vendor's own main location
 *            <id>    one branch
 *
 * The list comes from GET /api/vendor-team/:owner/locations, which the server
 * clamps to the login's branch access, together with the branch price switch.
 * The choice is remembered per signed-in person (utilities/identityMemory.js),
 * so a shared counter machine does not hand one cashier's branch to the next.
 * A member with exactly one location is locked to it.
 *
 * layouts/Admin.js keys the routed views on `active`, the same way it keys
 * them on the country, so every screen remounts and refetches for the branch
 * just picked instead of each view having to watch for the change.
 *
 * Only a SELLER login (an owner or a team member) has a vendor here; platform
 * staff get the inert default and choose a vendor on each screen as before.
 * ─────────────────────────────────────────────────────────────────────────────
 */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { apiVendorTeamLocations } from "../api/vendorTeam.js";
import { useVendorAccess } from "../hooks/useVendorAccess.js";
import { recall, remember } from "../utilities/identityMemory.js";
import { ALL, MAIN, locationKeyOf, outletIdParamOf } from "../utilities/vendorScope.js";

export const ACTIVE_BRANCH_KEY = "lusuku.activeBranch";

const BranchScopeContext = createContext(null);

const EMPTY_DIRECTORY = Object.freeze({
  loaded: false,
  vertical: null,
  parentId: null,
  locations: [],
  branchPricesEnabled: false,
});

const toLocations = (list) =>
  (Array.isArray(list) ? list : [])
    .filter((l) => l && l.id)
    .map((l) => ({
      id: locationKeyOf(l.id),
      name: l.name || (l.isMain || locationKeyOf(l.id) === MAIN ? "Main location" : "Branch"),
      address: l.address || null,
      isActive: l.isActive !== false,
      isMain: Boolean(l.isMain) || locationKeyOf(l.id) === MAIN,
    }));

/** Does a login see branches? See the context value's `hasBranches`. */
export const hasBranchesFor = (locations = [], isBranchScoped = false) =>
  (locations || []).some((l) => !l.isMain) || (Boolean(isBranchScoped) && (locations || []).length > 0);

/**
 * The location the dashboard should show, from what was asked for and what
 * this login may see. Pure, for the tests.
 *
 *   one location in scope (a branch member)  always that one (locked)
 *   "all" asked                              "all"
 *   asked a location that is not listed      "all", once the list is known
 *   otherwise                                as asked
 */
export const resolveActiveLocation = ({ requested, locations = [], isBranchScoped = false, loaded = false }) => {
  if (isBranchScoped && locations.length === 1) return locations[0].id;
  const key = locationKeyOf(requested);
  if (key === null) return ALL;
  if (loaded && !locations.some((l) => l.id === key)) return ALL;
  return key;
};

export const BranchScopeProvider = ({ children }) => {
  const access = useVendorAccess();
  const { vendorUserId, loaded: accessLoaded, isBranchScoped, branchScope } = access;
  const [directory, setDirectory] = useState(EMPTY_DIRECTORY);
  const [requested, setRequested] = useState(() => recall(ACTIVE_BRANCH_KEY, { storage: "local", fallback: ALL }));

  // The member's branch access, compared by value (refresh() after an owner
  // edits it in another tab).
  const scopeKey = JSON.stringify(branchScope || null);
  const load = useCallback(async () => {
    if (!vendorUserId) {
      setDirectory(EMPTY_DIRECTORY);
      return;
    }
    try {
      const { data } = await apiVendorTeamLocations(vendorUserId);
      setDirectory({
        loaded: true,
        vertical: data?.vertical || null,
        parentId: data?.parentId || null,
        locations: toLocations(data?.locations),
        branchPricesEnabled: data?.branchPricesEnabled === true,
      });
    } catch (err) {
      // A server without the branch API (or a refusal) reads as a vendor with
      // no branches: the picker stays hidden and every screen works as before.
      setDirectory({ ...EMPTY_DIRECTORY, loaded: true });
    }
  }, [vendorUserId]);

  // `scopeKey` re-reads the list when the member's branch access changes.
  useEffect(() => {
    if (!vendorUserId || !accessLoaded) return;
    load();
  }, [vendorUserId, accessLoaded, load, scopeKey]);

  const setActive = useCallback((next) => {
    const key = locationKeyOf(next);
    const value = key === null ? ALL : key;
    remember(ACTIVE_BRANCH_KEY, value, { storage: "local" });
    setRequested(value);
  }, []);

  const value = useMemo(() => {
    const { locations } = directory;
    const hasBranches = hasBranchesFor(locations, isBranchScoped);
    // Once the list is known, a login with no branch to pick reads the whole
    // book: a branch remembered from before its branches were deleted must
    // not keep filtering screens that no longer show a picker to change it.
    const active =
      directory.loaded && !hasBranches
        ? ALL
        : resolveActiveLocation({
            requested,
            locations,
            isBranchScoped,
            loaded: directory.loaded,
          });
    const byId = new Map(locations.map((l) => [l.id, l]));
    return {
      vendorUserId: vendorUserId || null,
      loaded: directory.loaded,
      vertical: directory.vertical,
      parentId: directory.parentId,
      locations,
      branchPricesEnabled: directory.branchPricesEnabled,
      // A vendor "has branches" when anything other than its main location
      // exists; without one the picker and the Branches tabs stay hidden.
      // A member limited to some branches always has them, even when their
      // only location is the main one: they still need to see which branch
      // they run and to reach its Branches tab.
      hasBranches,
      isBranchScoped: Boolean(isBranchScoped),
      // A member whose every branch has been deleted or taken away: nothing
      // to show, and the navbar says so instead of leaving empty screens.
      noBranchAccess: Boolean(isBranchScoped) && directory.loaded && locations.length === 0,
      locked: Boolean(isBranchScoped) && locations.length === 1,
      active,
      activeLocation: active === ALL ? null : byId.get(active) || null,
      outletIdParam: outletIdParamOf(active),
      nameOf: (id) => byId.get(locationKeyOf(id))?.name || null,
      setActive,
      refresh: load,
    };
  }, [directory, requested, isBranchScoped, vendorUserId, setActive, load]);

  return <BranchScopeContext.Provider value={value}>{children}</BranchScopeContext.Provider>;
};

const INERT = Object.freeze({
  vendorUserId: null,
  loaded: false,
  vertical: null,
  parentId: null,
  locations: [],
  branchPricesEnabled: false,
  hasBranches: false,
  isBranchScoped: false,
  noBranchAccess: false,
  locked: false,
  active: ALL,
  activeLocation: null,
  outletIdParam: undefined,
  nameOf: () => null,
  setActive: () => {},
  refresh: () => {},
});

/** The active location. Outside the provider it is the inert "all". */
export const useBranchScope = () => useContext(BranchScopeContext) || INERT;

/**
 * The locations of `vendorUserId` (an owner id), for a screen that may be
 * looking at another vendor than the signed-in one (platform staff on a
 * brand's catalogue). The signed-in vendor's own list comes from the context
 * with no second request.
 *
 * @returns {{ loaded, locations, branchPricesEnabled, hasBranches, vertical, parentId }}
 */
export const useVendorLocations = (vendorUserId, { parentId = null } = {}) => {
  const ctx = useBranchScope();
  const own = Boolean(vendorUserId) && ctx.vendorUserId === String(vendorUserId) && !parentId;
  const [state, setState] = useState(EMPTY_DIRECTORY);
  useEffect(() => {
    if (own || !vendorUserId) return undefined;
    let live = true;
    apiVendorTeamLocations(vendorUserId, { parentId })
      .then(({ data }) => {
        if (!live) return;
        setState({
          loaded: true,
          vertical: data?.vertical || null,
          parentId: data?.parentId || null,
          locations: toLocations(data?.locations),
          branchPricesEnabled: data?.branchPricesEnabled === true,
        });
      })
      .catch(() => live && setState({ ...EMPTY_DIRECTORY, loaded: true }));
    return () => {
      live = false;
    };
  }, [own, vendorUserId, parentId]);
  const source = own ? ctx : state;
  return {
    loaded: source.loaded,
    vertical: source.vertical,
    parentId: source.parentId,
    locations: source.locations,
    branchPricesEnabled: source.branchPricesEnabled,
    // The signed-in vendor's own answer comes from the context, so a branch
    // member whose only location is the main one keeps the Branches tab.
    hasBranches: own ? ctx.hasBranches : hasBranchesFor(source.locations),
  };
};

export default BranchScopeContext;
